import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  MapPin,
  Route,
  Building2,
  Gift,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Calendar,
  Activity,
  ArrowRight
} from 'lucide-react';
import { adminDashboardApi } from '../../services/adminApi';

const StatCard = ({ icon: Icon, label, value, change, changeType, color, link }) => (
  <Link to={link} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-slate-600 transition-all duration-200 group">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {change && (
        <div className={`flex items-center gap-1 text-sm ${changeType === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
          {changeType === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {change}
        </div>
      )}
    </div>
    <div className="mt-4">
      <p className="text-3xl font-bold text-white">{value}</p>
      <p className="text-slate-400 text-sm mt-1">{label}</p>
    </div>
    <div className="mt-4 flex items-center gap-2 text-sm text-slate-500 group-hover:text-emerald-400 transition-colors">
      Detayları Gör
      <ArrowRight className="w-4 h-4" />
    </div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await adminDashboardApi.getStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Hoş geldiniz! İşte platformunuzun genel durumu.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Toplam Kullanıcı"
          value={stats?.users?.total || 0}
          change={stats?.users?.new_this_week ? `+${stats.users.new_this_week} bu hafta` : null}
          changeType="up"
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          link="/admin/users"
        />
        <StatCard
          icon={MapPin}
          label="Mekanlar"
          value={stats?.content?.places || 0}
          color="bg-gradient-to-br from-emerald-500 to-teal-600"
          link="/admin/places"
        />
        <StatCard
          icon={Building2}
          label="İşletmeler"
          value={stats?.content?.businesses || 0}
          change={stats?.content?.pending_businesses ? `${stats.content.pending_businesses} onay bekliyor` : null}
          changeType="down"
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          link="/admin/businesses"
        />
        <StatCard
          icon={Gift}
          label="Aktif Kampanyalar"
          value={stats?.campaigns?.active || 0}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          link="/admin/campaigns"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Activity className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="font-semibold text-white">Kullanıcı Durumu</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Aktif</span>
              <span className="text-white font-medium">{stats?.users?.active || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Doğrulanmış</span>
              <span className="text-white font-medium">{stats?.users?.verified || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Bu Hafta Yeni</span>
              <span className="text-emerald-400 font-medium">+{stats?.users?.new_this_week || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Route className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="font-semibold text-white">İçerik</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Rotalar</span>
              <span className="text-white font-medium">{stats?.content?.routes || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Öne Çıkan Mekanlar</span>
              <span className="text-white font-medium">{stats?.content?.featured_places || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Onay Bekleyen İşletme</span>
              <span className="text-orange-400 font-medium">{stats?.content?.pending_businesses || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="font-semibold text-white">Topluluk</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Paylaşımlar</span>
              <span className="text-white font-medium">{stats?.community?.posts || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Yorumlar</span>
              <span className="text-white font-medium">{stats?.community?.comments || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Geziler</span>
              <span className="text-white font-medium">{stats?.community?.trips || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
        <h3 className="font-semibold text-white mb-4">Hızlı İşlemler</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link 
            to="/admin/places?action=new"
            className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-emerald-500/20 border border-slate-600 hover:border-emerald-500/50 rounded-xl transition-all duration-200 group"
          >
            <MapPin className="w-5 h-5 text-slate-400 group-hover:text-emerald-400" />
            <span className="text-slate-300 group-hover:text-white">Mekan Ekle</span>
          </Link>
          <Link 
            to="/admin/routes?action=new"
            className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-purple-500/20 border border-slate-600 hover:border-purple-500/50 rounded-xl transition-all duration-200 group"
          >
            <Route className="w-5 h-5 text-slate-400 group-hover:text-purple-400" />
            <span className="text-slate-300 group-hover:text-white">Rota Ekle</span>
          </Link>
          <Link 
            to="/admin/campaigns?action=new"
            className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-orange-500/20 border border-slate-600 hover:border-orange-500/50 rounded-xl transition-all duration-200 group"
          >
            <Gift className="w-5 h-5 text-slate-400 group-hover:text-orange-400" />
            <span className="text-slate-300 group-hover:text-white">Kampanya</span>
          </Link>
          <Link 
            to="/admin/businesses?status=pending"
            className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-blue-500/20 border border-slate-600 hover:border-blue-500/50 rounded-xl transition-all duration-200 group"
          >
            <Building2 className="w-5 h-5 text-slate-400 group-hover:text-blue-400" />
            <span className="text-slate-300 group-hover:text-white">Onay Bekleyenler</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
